import { Cross2Icon } from "@radix-ui/react-icons";
import { Avatar, Box, Button, Dialog, Flex, IconButton, Text } from "@radix-ui/themes";
import { type FormEvent, type ReactNode, useRef, useState } from "react";
import type { UpgradeSurface } from "../../../../utils/conversion-shared";
import { useAuth } from "../../contexts/AuthContext";
import { useUsage } from "../../contexts/UsageContext";
import { useUserDevices } from "../../hooks/useUserDevices";
import { accountCheckReason, knownDeviceCount, type UpgradeAccountCheck, upgradeAccountCheck } from "../../utils/accountCheck";
import { trackUpgradeAccountCheck } from "../../utils/conversionAnalytics";
import { emailInitials } from "../../utils/ui";
import { PANEL_STYLE } from "../dashboard/tokens";
import ChatGptAccountsPath from "../shared/ChatGptAccountsPath";
import { OneAccountGuideLink } from "../shared/OneAccountGuide";

const LIST_STYLE = {
    margin: "8px 0 0",
    paddingLeft: 20,
    display: "grid",
    gap: 6,
};
/** Full width when stacked on a phone, natural width side by side. */
const ACTION_WIDTH = {
    initial: "100%",
    xs: "auto",
} as const;

/**
 * "Is this the right account?" before Stripe opens, for an account with no devices or no tool calls
 * this month (see utils/accountCheck). Every upgrade form calls holdCheckout first in its onSubmit
 * and renders the dialog next to itself.
 *
 * A held submit is replayed with requestSubmit() when the user continues, so the form's own submit
 * handler (upgrade_clicked, the checkout lock, the Stripe tab) runs exactly as it does without the
 * check. The replay happens inside the dialog button's click, so the browser still allows the new tab.
 */
export function useUpgradeAccountCheck(surface: UpgradeSurface): {
    holdCheckout: (event: FormEvent<HTMLFormElement>) => boolean;
    dialog: ReactNode;
} {
    const { user, signOut } = useAuth();
    const { usage, conversionContext } = useUsage();
    const { devices, known: devicesKnown } = useUserDevices();
    const [open, setOpen] = useState(false);
    // Kept after closing, so the dialog's text does not change while it animates out.
    const [shownCheck, setShownCheck] = useState<UpgradeAccountCheck | null>(null);
    const heldForm = useRef<HTMLFormElement | null>(null);
    const replaying = useRef(false);
    // Set when the guide link closes the dialog: focus then stays on the guide it opens, instead of
    // returning to the Upgrade button.
    const leavingForGuide = useRef(false);

    const email = user?.email;

    const holdCheckout = (event: FormEvent<HTMLFormElement>) => {
        const check = upgradeAccountCheck(knownDeviceCount(devicesKnown, devices), usage ? usage.callsUsed : null);
        if (replaying.current || !email || !check) {
            return false;
        }
        event.preventDefault();
        heldForm.current = event.currentTarget;
        setShownCheck(check);
        setOpen(true);
        void trackUpgradeAccountCheck("shown", {
            surface,
            conversionContext,
            check,
        });
        return true;
    };

    const upgrade = () => {
        const form = heldForm.current;
        heldForm.current = null;
        setOpen(false);
        if (shownCheck) {
            void trackUpgradeAccountCheck("upgraded", {
                surface,
                conversionContext,
                check: shownCheck,
            });
        }
        if (!form?.isConnected) {
            return;
        }
        replaying.current = true;
        try {
            form.requestSubmit();
        } finally {
            replaying.current = false;
        }
    };

    const switchAccount = () => {
        setOpen(false);
        if (shownCheck) {
            void trackUpgradeAccountCheck("switched", {
                surface,
                conversionContext,
                check: shownCheck,
            });
        }
        void signOut("signin");
    };

    const openGuide = () => {
        leavingForGuide.current = true;
        setOpen(false);
    };

    const dialog = (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Content
                maxWidth="480px"
                onCloseAutoFocus={(event) => {
                    if (leavingForGuide.current) {
                        leavingForGuide.current = false;
                        event.preventDefault();
                    }
                }}
            >
                <Flex justify="between" align="start" gap="3">
                    <Dialog.Title>Is this the right account?</Dialog.Title>
                    <Dialog.Close>
                        <IconButton type="button" variant="ghost" color="gray" aria-label="Close">
                            <Cross2Icon aria-hidden="true" />
                        </IconButton>
                    </Dialog.Close>
                </Flex>
                {/* Reads top down: which account, why it matters, where to check. The account is shown like
                    the sidebar's account row, so it reads as "this account" at a glance. */}
                <Flex align="center" gap="3" p="3" style={PANEL_STYLE}>
                    <Avatar size="2" radius="full" fallback={emailInitials(email)} aria-hidden="true" />
                    <Box
                        style={{
                            minWidth: 0,
                        }}
                    >
                        <Text
                            as="div"
                            size="2"
                            weight="medium"
                            style={{
                                overflowWrap: "anywhere",
                            }}
                        >
                            {email}
                        </Text>
                        <Text as="div" size="1" color="gray">
                            {shownCheck ? accountCheckReason(shownCheck) : null}
                        </Text>
                    </Box>
                </Flex>
                <Dialog.Description size="2" mt="4">
                    Pro only works for the account ChatGPT and your devices are connected to. If they use another email, switch to that account before
                    you upgrade.
                </Dialog.Description>
                <Text as="p" size="2" mt="4">
                    Not sure which account you use? Check both places. They should both show the email you want to use Desktop Commander with:
                </Text>
                <ul style={LIST_STYLE}>
                    <li>
                        <Text size="2">
                            <strong>ChatGPT:</strong> <ChatGptAccountsPath />
                        </Text>
                    </li>
                    <li>
                        <Text size="2">
                            <strong>Terminal:</strong> the User line under "Device ready" in the window where Desktop Commander runs
                        </Text>
                    </li>
                </ul>
                <Text as="p" size="2" mt="4">
                    <OneAccountGuideLink onClick={openGuide} />
                </Text>
                {/* Primary on the right, as in the dashboard's other dialogs. On a phone the two stack
                    full width and the primary goes on top. Short labels: the account is named above. */}
                <Flex
                    direction={{
                        initial: "column-reverse",
                        xs: "row",
                    }}
                    justify="end"
                    gap="3"
                    mt="5"
                >
                    <Box width={ACTION_WIDTH}>
                        <Button
                            type="button"
                            variant="soft"
                            color="gray"
                            onClick={switchAccount}
                            style={{
                                width: "100%",
                            }}
                        >
                            Switch account
                        </Button>
                    </Box>
                    <Box width={ACTION_WIDTH}>
                        <Button
                            type="button"
                            onClick={upgrade}
                            style={{
                                width: "100%",
                            }}
                        >
                            Continue to checkout
                        </Button>
                    </Box>
                </Flex>
            </Dialog.Content>
        </Dialog.Root>
    );

    return {
        holdCheckout,
        dialog,
    };
}
